<template>
  <v-container fluid justify-start>
    
    <!-- Page Title -->
    <app-page-title
      v-bind:title="'Herd Sort Queries'"
      v-bind:icon="'query_builder'"
      v-bind:actionLink="!isSmallDevice ? 'Go To Herd Sort' : 'Herd Sort'"
      v-bind:actionLinkHidden="isLoading || !isUserHerd"
      @action-link-click="actionLinkClicked"
    />

    <!-- No Herds Assigned -->
    <v-alert v-if="!isLoading && !isUserHerd" type="error" border="right" colored-border prominent>
      <v-row justify="center">
        <span class="title">You have no herds assigned</span>
        <v-icon class="ml-2" large>
          sentiment_very_dissatisfied
        </v-icon>
      </v-row>
    </v-alert>

    <template v-else>

      <!-- Data Table -->
      <v-data-table
        :headers="headers"
        :items="queries"
        :loading="isLoading"
        :search="search"
        :no-data-text="noDataText"
        item-key="id"
        mobile-breakpoint="500"
        >

        <v-progress-linear v-slot:progress :color="appColor_Progress" indeterminate />

        <!-- Header Toolbar -->
        <template v-slot:top>
          <v-toolbar dark :color="appColor_Medium">

            <!-- Search -->
            <v-text-field
              v-model="search"
              class="pt-0 pb-0"
              append-icon="search"
              label="Search"
              single-line
              hide-details
              clearable
            />

            <v-spacer />

            <!-- Edit Dialog -->
            <v-dialog
              v-model="editDialog"
              max-width="600px"
              :persistent="true"
              >
              <template v-slot:activator="{ on }">
                <v-btn
                  :small="isSmallDevice"
                  outlined
                  v-on="on"
                  :disabled="isLoading"
                  class="ml-3"
                  >
                  New Query
                </v-btn>
              </template>
              <v-card>
                <v-toolbar
                  dark
                  flat
                  :color="appColor_Light"
                  >
                  <v-icon class="mr-2">
                    query_builder
                  </v-icon> 
                  <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                  <v-container
                    grid-list-md
                    class="pa-1"
                    >
                    <form>
                      <v-row>
                        <v-col
                          cols="12"
                          sm="12"
                          md="12"
                          >
                          <v-text-field
                            v-model="editedQuery.title"
                            v-validate="'max:50|required'"
                            label="Title"
                            :error-messages="errors.collect('title')"
                            data-vv-name="title"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="12"
                          md="12"
                          >
                          <v-text-field
                            v-model="editedQuery.description"
                            v-validate="'max:200'"
                            label="Description"
                            flat
                            no-resize
                            clearable
                            :error-messages="errors.collect('description')"
                            data-vv-name="description"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="12"
                          md="12"
                          >
                          <v-select
                            v-model="editedQuery.herdName"
                            v-validate="'required'"
                            :items="herdNames"
                            label="Herd"
                            :error-messages="errors.collect('herdName')"
                            data-vv-name="herdName"
                            @change="herdChanged"
                            :disabled="herdDisabled"
                          />
                        </v-col>
                        <v-col
                          cols="6"
                          sm="6"
                          md="4"
                          >
                          <v-select
                            v-model="editedQuery.animalTypeId"
                            v-validate="'required'"
                            :items="animalTypes"
                            label="Type"
                            :error-messages="errors.collect('animalType')"
                            data-vv-name="animalType"
                            @change="animalTypeChanged"
                            :disabled="animalTypeDisabled"
                          />
                        </v-col>
                        <v-col
                          cols="6"
                          sm="6"
                          md="4"
                          >
                          <v-select
                            v-model="editedQuery.gender"
                            v-validate="'required'"
                            :items="genders"
                            label="Gender"
                            :error-messages="errors.collect('gender')"
                            data-vv-name="gender"
                            @change="genderChanged"
                            :disabled="isGenderDisabled"
                          />
                        </v-col>
                        <v-col
                          cols="6"
                          sm="6"
                          md="4"
                          >
                          <v-select
                            v-model="editedQuery.birthYear"
                            v-validate="'required'"
                            :items="birthYears"
                            label="Year"
                            :error-messages="errors.collect('year')"
                            data-vv-name="year"
                            :disabled="yearDisabled"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    :disabled="isSaving"
                    text
                    @click.native="closeEditDialog"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    :disabled="isSaving || saveDisabled"
                    text
                    @click.native="saveEdit"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar>
        </template>

        <template v-slot:item.details="{ item }">
          <v-btn icon @click="editDetail(item)">
            <v-icon>details</v-icon>
          </v-btn>
        </template>

        <template v-slot:item.title="{ item }">
          <router-link to="/herdSortManager">
            <span @click="edit(item)">{{ item.title }}</span>
          </router-link>
        </template>

        <template v-slot:item.animalType="{ item }">
          <span class="text-xs-left">{{ getAnimalTypeDescription(item) }}</span>
        </template>

        <template v-slot:item.action="{ item }">
          <span class="justify-center">
            <v-icon @click="remove(item)">delete</v-icon>
          </span>
        </template>
      </v-data-table>
    </template>

    <!-- Criteria Dialog -->
    <v-dialog
      v-model="detailDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      >
      <user-query-criteria
        v-bind:control-id="selectedQueryInfo.id"
        v-bind:query-info="selectedQueryInfo"
        v-bind:user-trait-ids="selectedTraitIds"
        v-bind:filter-criteria="filterCriteriaOriginal"
        v-bind:optional-filter-criteria="optionalFilterCriteriaOriginal"
        v-bind:trait-criteria="traitCriteriaOriginal"
        v-bind:filter-rules="filterRules"
        v-bind:group-filter-rules="groupFilterRules"
        v-bind:birth-years="birthYears"
        v-bind:herds="herds"
        v-bind:scope="scope"
        v-bind:is-add-mode="isAddMode"
        v-bind:is-breeder="isAdminy"
        @cancel="onCriteriaSelectorCancel"
        @save="onCriteriaSelectorSave"
      />
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog
      v-model="errorDialog"
      max-width="500px"
      >
      <app-error
        :error-title="errorTitle"
        :error-text="errorText"
        @close="errorDialog = false"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import AppPageTitle from '@/components/AppPageTitle'
import UserQueryCriteria from '@/components/UserQueryCriteria'
import AppError from '@/components/AppError'
import { mapGetters } from 'vuex'
import { AppColor, AnimalTypeId, QueryTypeId, AnimalSourceId, FilterName } from '@/config.js'

export default {
  name: 'QueryManager',
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    AppPageTitle,
    AppError,
    UserQueryCriteria
  },
  data () {
    return {
      appColor_Light: AppColor.Light,
      appColor_Medium: AppColor.Medium,
      appColor_Background: AppColor.Background,
      appColor_ContentToolbar: AppColor.ContentToolbar,
      appColor_Progress: AppColor.Progress,
      
      // animal source id
      animalSourceId_Herdtrax: AnimalSourceId.Herdtrax,

      // query type id
      QueryTypeId_Custom: QueryTypeId.Custom,
      QueryTypeId_Template: QueryTypeId.Template,

      // filters
      filter_Herd: FilterName.Herd,
      filter_BirthYear: FilterName.BirthYear,
      filter_AnimalType: FilterName.AnimalType,
      filter_Gender: FilterName.Gender,

      // animal types
      animalTypeId_Calf: AnimalTypeId.Calf,
      animalTypeId_Dam: AnimalTypeId.Dam,
      animalTypeId_Sire: AnimalTypeId.Sire,
      animalTypeId_Steer: AnimalTypeId.Steer,

      scope: 'herdtrax',
      isLoading: true,
      isSaving: false,
      isAddMode: false,
      isUserHerd: false,
      editDialog: false,
      detailDialog: false,
      confirmDialog: false,
      search: '',
      filterCriteria: [],
      optionalFilterCriteria: [],
      traitCriteria: [],
      queries: [],
      filterCriteriaOriginal: [],
      optionalFilterCriteriaOriginal: [],
      traitCriteriaOriginal: [],
      selectedTraitIds: [],
      selectedOptionalFilterIds: [],
      filterRules: [],
      groupFilterRules: [],
      herds: [],
      isGenderDisabled: true,

      // for the criteria picker
      selectedQueryInfo: {
        id: 0,
        title: null,
        description: null,
        colourCode: null,
        cosmosCode: null
      },
 
      headers: [
        {
          text: 'Detail',
          value: 'details',
          align: 'center',
          sortable: false,
          width: '5%'
        },
        {
          text: 'Title',
          value: 'title',
          align: 'left',
          width: '20%'
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          width: '30%'
        },
        {
          text: 'Herd',
          value: 'herdName',
          align: 'left',
          width: '20%'
        },
        {
          text: 'Year',
          value: 'year',
          align: 'left',
          width: '5%'
        },
        {
          text: 'Type',
          value: 'animalType',
          align: 'left',
          width: '15%'
        },
        {
          value: 'action',
          sortable: false,
          width: '5%'
        }
      ],
      editedIndex: -1,
      editedQuery: {
        id: -1,
        userId: null,
        title: '',
        description: '',
        herdName: '',
        birthYear: '',
        animalTypeId: '',
        gender: 'A'
      },
      defaultQuery: {
        id: -1,
        userId: null,
        title: '',
        description: '',
        herdName: '',
        birthYear: '',
        animalTypeId: '',
        gender: 'A'
      },
      dictionary: {
        custom: {
          title: {
            required: () => 'Title is required',
            max: 'Title must be less than 50 characters'
          },
          description: {
            max: 'Description must be less than 200 characters'
          },
          herdName: {
            required: () => 'Herd is required'
          },
          year: {
            required: () => 'Year is required'
          },
          animalType: {
            required: () => 'Animal Type is required'
          },
          gender: {
            required: () => 'Gender is required'
          }
        }
      },

      // error handling
      errorDialog: false,
      errorTitle: 'Internal Error',
      isError: false,
      errorText: null
    }
  },
  computed: {
    ...mapGetters(['profile']),
    noDataText () {
      return this.isLoading
        ? 'One moment...'
        : 'No queries found'
    },
    formTitle () {
      return this.editedQuery.id > 0 ? 'Edit Query' : 'New Query'
    },
    herdNames () {
      const self = this
      const filters = self.filterCriteria.filter(function (value) {
        return value.columnName === self.filter_Herd
      })

      if (filters.length > 0) {
        return filters[0].dropdownItems
      }
      return []
    },
    birthYears () {
      const self = this
      if (self.editedIndex >= 0) {
        return [{ value: self.editedQuery.birthYear, text: self.editedQuery.birthYear }]
      }

      var animalTypeId = null
      if (self.editedQuery.animalTypeId !== '') {
        animalTypeId = parseInt(self.editedQuery.animalTypeId)
      }

      var gender = null
      if (self.editedQuery.gender !== '') {
        gender = self.editedQuery.gender
      }

      var herd = null
      if (self.editedQuery.herdName !== '') {
        herd = self.editedQuery.herdName
      }

      var hid = null
      if (self.herds.length > 0) {
        if (herd !== null) {
          hid = parseInt(self.herds.filter(function (value) {
            return value.name === herd
          })[0].id)
        } else {
          hid = self.herds[0].id
        }
      }

      const rules = self.filterRules.filter(function (value) {
        return (value.hid === hid || hid === null) &&
          (value.animalTypeId === animalTypeId || animalTypeId === null) &&
          (value.gender === gender || gender === null || gender === 'A')
      })
      var result = rules.length > 1 ? [{ value: 'A', text: 'Any' }] : []
      result = rules.map(r => {
        return {
          value: r.birthYear.toString(),
          text: r.birthYear.toString()
        }
      })
      return result
    },
    animalTypes () {
      const self = this
      if (self.editedIndex >= 0) {
        return [{ value: self.editedQuery.animalTypeId, text: self.editedQuery.animalTypeId }]
      }

      const filters = self.filterCriteria.filter(function (value) {
        return value.columnName === self.filter_AnimalType
      })

      if (filters.length > 0) {
        return filters[0].dropdownItems
      }
      return []
    },
    genders () {
      const self = this
      if (self.editedIndex >= 0) {
        return [{ value: self.editedQuery.gender, text: self.editedQuery.gender }]
      }

      const filters = self.filterCriteria.filter(function (value) {
        return value.columnName === self.filter_Gender
      })

      if (filters.length > 0) {
        return filters[0].dropdownItems
      }
      return []
    },
    useGroupFilterLabelClass () {
      return {
        'label': !this.isUseGroupFilteringDisabled,
        'label-disabled': this.isUseGroupFilteringDisabled
      }
    },
    saveDisabled () {
      return (this.birthYears.length === 0 && this.editedQuery.id <= 0)
    },
    isAdmin () {
      return this.profile.roles !== null && this.profile.roles.includes('Admin')
    },
    isBreeder () {
      return this.profile.roles !== null && this.profile.roles.includes('Breeder')
    },
    isAdminy () {
      return this.isAdmin || this.isBreeder
    },
    herdDisabled () {
      return this.herds.length === 0 || this.editedIndex >= 0
    },
    animalTypeDisabled () {
      return this.animalTypes.length === 0 || this.editedIndex >= 0
    },
    yearDisabled () {
      return this.birthYears.length === 0 || this.editedIndex >= 0
    },
    isSmallDevice () {
      return this.$vuetify.breakpoint.name === 'xs' || this.$vuetify.breakpoint.name === 'sm'
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true
      appService.getHerdSortQueries(self.profile.id, self.isBreeder).then(data => {
        if (data.errorSeverity === 0) {
          self.isUserHerd = false
        } else if (data.errorSeverity > 0) {
          self.isError = true
          self.errorText = data.errorMessage
          self.errorDialog = true
        } else {
          self.isUserHerd = true
          self.queries = data.queries
          self.filterCriteria = data.filterCriteria
          self.optionalFilterCriteria = data.optionalFilterCriteria
          self.traitCriteria = data.traitCriteria
          self.filterRules = data.filterRules
          self.groupFilterRules = data.groupFilterRules
          self.herds = data.herds
        }
        self.isLoading = false
      })
    },

    edit (item) {
      const self = this
      self.editedIndex = self.queries.indexOf(item)
      self.editedQuery = Object.assign({}, item)
      self.editedQuery.birthYear = item.year
      self.editedQuery.animalTypeId = item.animalType
      self.editDialog = true
    },

    remove (item) {
      const self = this
      const index = self.queries.indexOf(item)
      const queryTitle = self.queries[index].title
      
      self.$confirm('<p class="subtitle-1 pt-5">' + queryTitle + '</p>', {
        color: 'red darken-3',
        icon: 'help',
        title: 'Delete Query?',
        width: 300,
        persistent: true
      }).then(res => {
        if (res) {
          self.isSaving = true
          self.confirmDialog = false
          appService.deleteUserQuery(item.id).then(() => {
            self.queries.splice(index, 1)
            self.isSaving = false
            self.editedIndex = -1
          })
        }
      }).catch(() => {
        self.isSaving = false
        self.editedIndex = -1
      })
    },

    saveEdit () {
      const self = this
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          if (self.editedQuery.id > 0) {
            appService.updateUserQuery(self.editedQuery.id, self.getUpdateModel(self.editedQuery), self.scope).then((data) => {
              self.isSaving = false
              self.queries[self.editedIndex].title = data.title
              self.queries[self.editedIndex].description = data.description
              self.closeEditDialog()
              self.editedIndex = -1
            }).catch(() => {
              self.isSaving = false
              self.closeEditDialog()
              self.editedIndex = -1
            })
          } else {
            self.isAdding = true
            appService.addUserQuery(self.getAddModel(self.editedQuery)).then((data) => {
              const newId = data.id
              if (newId > 0) {
                appService.initQueryFilters(newId, self.getFilterSelectionModels(), self.scope).then((data) => {
                  self.editedQuery = data
                  self.queries.push(self.editedQuery)
                  self.editedIndex = self.queries.length - 1
                  self.isSaving = false
                  self.closeEditDialog()
                  self.isAddMode = true
                  self.editDetail()
                }).catch(() => {
                  self.isSaving = false
                  self.closeEditDialog()
                  self.editedIndex = -1
                })
              }
            }).catch(() => {
              self.isSaving = false
              self.closeEditDialog()
              self.editedIndex = -1
            })
          }
        }
      })
    },

    getAddModel (q) {
      return {
        userId: this.profile.id,
        title: this.trimText(q.title),
        description: this.trimText(q.description),
        animalSourceId: this.animalSourceId_Herdtrax,
        queryTypeId: this.QueryTypeId_Custom
      }
    },

    getUpdateModel (q) {
      return {
        id: q.id,
        userId: this.profile.id,
        title: this.trimText(q.title),
        description: this.trimText(q.description)
      }
    },

    trimText(text) {
      return !this.isNullOrUndefined(text)
        ? text.length > 0 ? text.trim() : null
        : null
    },

    getFilterSelectionModels () {
      var result = [
        {
          columnName: this.filter_Herd,
          value: this.editedQuery.herdName
        },
        {
          columnName: this.filter_BirthYear,
          value: this.editedQuery.birthYear
        },
        {
          columnName: this.filter_AnimalType,
          value: this.editedQuery.animalTypeId
        },
        {
          columnName: this.filter_Gender,
          value: this.editedQuery.gender
        }
      ]
      return result
    },

    closeEditDialog () {
      const self = this
      self.editDialog = false
      setTimeout(() => {
        self.editedQuery = Object.assign({}, self.defaultQuery)
        this.editedIndex = -1
        self.$validator.reset()
      }, 300)
    },

    editDetail (item) {
      const self = this
      // will be 'undefined' if called after add new
      if (typeof item !== 'undefined') {
        self.editedIndex = self.queries.indexOf(item)
      }
      if (self.editedIndex >= 0) {
        const idx = self.editedIndex
        self.filterCriteriaOriginal = []
        self.traitCriteriaOriginal = []
        self.filterCriteriaOriginal = self.cloneFilterCriteria(self.queries[idx].filterSelections)
        self.traitCriteriaOriginal = self.cloneTraitCriteria(self.queries[idx].traitIds)
        self.selectedTraitIds = self.queries[idx].traitIds.map(id => id)

        const herd = self.queries[idx].filterSelections.filter(function (value) {
          return value.columnName === self.filter_Herd
        })[0].value
        const hid = parseInt(self.herds.filter(function (value) {
          return value.name === herd
        })[0].id)
        const birthYear = self.queries[idx].filterSelections.filter(function (value) {
          return value.columnName === self.filter_BirthYear
        })[0].value
        const animalTypeId = parseInt(self.queries[idx].filterSelections.filter(function (value) {
          return value.columnName === self.filter_AnimalType
        })[0].value)
        const gender = self.queries[idx].filterSelections.filter(function (value) {
          return value.columnName === self.filter_Gender
        })[0].value

        self.enableOptionalFilters(hid, birthYear, animalTypeId, gender)
        self.optionalFilterCriteriaOriginal = self.cloneOptionalFilterCriteria(self.queries[idx].optionalFilterIds)

        self.selectedQueryInfo = {
          id: self.queries[idx].id,
          title: self.queries[idx].title,
          description: self.queries[idx].description,
          colourCode: self.queries[idx].colourCode,
          cosmosCode: 'HX'
        }

        self.detailDialog = true
      }
    },

    cloneTraitCriteria (traitIds) {
      var result = []
      this.traitCriteria.forEach(g => {
        var traits = []
        traits = g.traits.map(gt => {
          return {
            id: gt.id,
            description: gt.description,
            sequence: gt.sequence,
            isBreeder: gt.isBreeder,
            selected: traitIds.includes(gt.id)
          }
        })
        result.push({
          id: g.id,
          groupName: g.groupName,
          sequence: g.sequence,
          traits: traits
        })
      })
      return result
    },

    cloneOptionalFilterCriteria (optionalFilterIds) {
      const filters = this.optionalFilterCriteria
      const result = filters.map(f => {
        return {
          id: f.id,
          description: f.description,
          selected: optionalFilterIds.includes(f.id),
          disabled: f.disabled
        }
      })
      return result
    },

    cloneFilterCriteria (filterSelections) {
      const self = this
      var result = []
      const filters = self.filterCriteria

      for (var key1 in filters) {
        var dropdownItems = []
        const filterDropdowns = filters[key1].dropdownItems
        for (var key2 in filterDropdowns) {
          dropdownItems.push({
            value: filterDropdowns[key2].value,
            text: filterDropdowns[key2].text
          })
        }
        const filterColumn = filters[key1].columnName
        const filterValue = filterSelections.filter(f => { return f.columnName === filterColumn })[0].value

        // need to disable Gender if AnimalType is "Calf"
        const animalTypeId = filterSelections.filter(function (value) {
          return value.columnName === self.filter_AnimalType
        })[0].value

        var isDisabled = false
        if (filterColumn === self.filter_Gender) {
          isDisabled = parseInt(animalTypeId) !== self.animalTypeId_Calf
        }
        result.push({
          id: filters[key1].id,
          description: filters[key1].description,
          inputTypeId: filters[key1].inputTypeId,
          columnName: filterColumn,
          minValue: filterValue,
          maxValue: filterValue,
          isDisabled: isDisabled,
          dropdownItems: dropdownItems
        })
      }
      return result
    },

    isNullOrUndefined (obj) {
      return obj === undefined 
        || typeof obj === 'undefined' 
        || obj === null
    },

    // event handlers
    onCriteriaSelectorSave ({ userQueries }) {
      const idx = this.editedIndex >= 0
        ? this.editedIndex
        : userQueries.length - 1

      this.queries = userQueries
      this.traitCriteriaOriginal = []
      this.filterCriteriaOriginal = []
      this.filterCriteriaOriginal = this.cloneFilterCriteria(this.queries[idx].filterSelections)
      this.traitCriteriaOriginal = this.cloneTraitCriteria(this.queries[idx].traitIds)
      this.detailDialog = false
      this.isAddMode = false
      this.editedIndex = -1
    },

    onCriteriaSelectorCancel () {
      this.detailDialog = false
      this.isAddMode = false
      this.editedIndex = -1
    },

    animalTypeChanged () {
      if (this.editedIndex > 0) {
        this.isGenderDisabled = true
        return true
      }

      const animalTypeId = parseInt(this.editedQuery.animalTypeId)

      if (animalTypeId === this.animalTypeId_Calf) {
        this.editedQuery.gender = 'A'
        this.isGenderDisabled = false
      }
      if (animalTypeId === this.animalTypeId_Dam) {
        this.editedQuery.gender = 'F'
        this.isGenderDisabled = true
      }
      if (animalTypeId === this.animalTypeId_Sire) {
        this.editedQuery.gender = 'M'
        this.isGenderDisabled = true
      }
      if (animalTypeId === this.animalTypeId_Sire) {
        this.editedQuery.gender = 'M'
        this.isGenderDisabled = true
      }
      if (this.editedQuery.herdName.length > 0 &&
        this.birthYears.length > 0 &&
        !this.birthYears.includes(this.editedQuery.birthYear)) {
        this.editedQuery.birthYear = this.birthYears[0].value
      }
    },

    herdChanged () {
      if (this.editedQuery.animalTypeId.length > 0 &&
        this.birthYears.length > 0 &&
        !this.birthYears.includes(this.editedQuery.birthYear)) {
        this.editedQuery.birthYear = this.birthYears[0].value
      }
    },

    genderChanged () {
      if (this.birthYears.length > 0 &&
        this.editedQuery.herdName.length > 0 &&
        this.editedQuery.animalTypeId.length > 0 &&
        !this.birthYears.includes(this.editedQuery.birthYear)) {
        this.editedQuery.birthYear = this.birthYears[0].value
      }
    },

    enableOptionalFilters (hid, birthYear, animalTypeId, gender) {
      const self = this
      const optionalFilters = self.optionalFilterCriteria

      optionalFilters.forEach(f => {
        const optionalFilter = f
        optionalFilter.disabled = self.isOptionalFilterDisabled(hid, birthYear, animalTypeId, gender, f.id)
      })
    },

    isOptionalFilterDisabled (hid, birthYear, animalTypeId, gender, filterId) {
      const self = this
      return self.groupFilterRules.filter(function (value) {
        return value.criterionId === filterId &&
          value.hid === hid &&
          (value.birthYear === parseInt(birthYear) || birthYear === 'A') &&
          value.animalTypeId === animalTypeId &&
          (value.gender === gender || gender === 'A')
      }).length === 0
    },

    getAnimalTypeDescription (q) {
      const self = this
      const animalTypeId = q.filterSelections.filter(function (value) {
        return value.columnName === self.filter_AnimalType
      })[0].value

      const gender = q.filterSelections.filter(function (value) {
        return value.columnName === self.filter_Gender
      })[0].value

      if (parseInt(animalTypeId) === self.animalTypeId_Calf) {
        if (gender !== 'A') {
          return q.animalType + '-' + gender
        }
      }
      return q.animalType
    },

    actionLinkClicked () {
      this.$router.push('/herdSort')
    }
  },
  created () {
    this.loadData()
  },
  mounted () {
    this.$validator.localize('en', this.dictionary)
  },
  watch: {
    editDialog (val) {
      val || this.closeEditDialog()
    }
  }
}
</script>

